import React, { useState, useRef, useCallback } from "react";
import { Link } from "react-router-dom";
import { HashLink } from "react-router-hash-link";
import { rounding } from "../../utils/calculators/general";
import { validateHeightField } from "../../utils/calculators/validateHeightField";
import { isNumber } from "../../utils/isNumber";
import { formatDoseDisplay, formatWeightDisplay } from "../../utils/formatters/clinicalDisplay";
import CalculatorTemplate from "../../components/calculators/CalculatorTemplate";
import CalculatorPanel from "../../components/calculators/CalculatorPanel";
import CalculatorGenderSelector from "../../components/calculators/CalculatorGenderSelector";
import CalculatorInput from "../../components/calculators/CalculatorInput";
import HeightInputGroup from "../../components/calculators/HeightInputGroup";
import WeightInputGroup from "../../components/calculators/WeightInputGroup";
import CreatinineInputGroup from "../../components/calculators/CreatinineInputGroup";
import AntibioticsSelector from "../../components/calculators/AntibioticsSelector";
import {
  useHeightInput,
  useWeightInput,
  useSerumCreatinineInput,
} from "../../hooks/calculatorHooks";
import { getHeightInCm } from "../../utils/lengthConverter";
import { getWeightInKg } from "../../utils/weightConverter";
import CalculatorResults from "../../components/calculators/CalculatorResults";
import styles from "../../components/calculators/calculator.module.scss";
import useErrorFocus from "../../hooks/useErrorFocus";

import { calculateAminoglycosideInitialDose } from "../../utils/calculators/aminoglycoside";
import {
  checkNum,
  saneAge,
  saneSerum,
  saneWeight,
} from "../../utils/calculators/validation";

const INITIAL_INPUTS = {
  antibiotic: "",
  weight: "",
  height: "",
  age: "",
  sex: "",
  critical: null,
  serumCreatinine: "",
  renalUnknown: false,
};

function displayValue(val, unit, decimals = 0) {
  return isNumber(val)
    ? `${rounding(val, decimals)}${unit ? ` ${unit}` : ""}`
    : "-";
}

const AbxCalcApp = ({ isWebLayout = false, currentPath = "" }) => {
  const [inputs, setInputs] = useState({ ...INITIAL_INPUTS });
  const [errors, setErrors] = useState({});
  const [dose, setDose] = useState("");
  const [showResult, setShowResult] = useState(false);
  const [calcResult, setCalcResult] = useState(null);
  const resultsRef = useRef(null);
  const calcDetailsRef = useRef(null);

  const fieldOrder = ['antibiotic', 'sex', 'height', 'weight', 'age', 'serumCreatinine', 'critical'];

  useErrorFocus(errors, fieldOrder);

  const {
    weightValue,
    weightUnit,
    handleWeightChange,
    handleWeightUnitChange,
    clearWeightFields,
    unitOptions: weightUnitOptions,
  } = useWeightInput();

  const {
    height: heightValue,
    heightUnit,
    feet: feetValue,
    inches: inchesValue,
    handleHeightChange,
    handleFeetChange,
    handleInchesChange,
    handleUnitChange: handleHeightUnitChange,
    clearHeightFields: resetHeight,
    unitOptions: heightUnits,
  } = useHeightInput("cm");

  const {
    serumCreatinineValue,
    serumCreatinineUnit,
    handleSerumCreatinineChange,
    handleSerumCreatinineUnitChange,
    clearSerumCreatinineFields,
  } = useSerumCreatinineInput();

  const wrappedHeightChange = useCallback(
    (e) => handleHeightChange(e, setErrors),
    [handleHeightChange],
  );
  const wrappedFeetChange = useCallback(
    (e) => handleFeetChange(e, setErrors),
    [handleFeetChange],
  );
  const wrappedInchesChange = useCallback(
    (e) => handleInchesChange(e, setErrors),
    [handleInchesChange],
  );
  const wrappedUnitChange = useCallback(
    (e) => handleHeightUnitChange(e, setErrors),
    [handleHeightUnitChange],
  );
  const wrappedWeightChange = useCallback(
    (e) => handleWeightChange(e, setErrors),
    [handleWeightChange],
  );
  const wrappedWeightUnitChange = useCallback(
    (e) => handleWeightUnitChange(e, setErrors),
    [handleWeightUnitChange],
  );
  const wrappedCreatinineUnitChange = useCallback(
    (e) => handleSerumCreatinineUnitChange(e, setErrors),
    [handleSerumCreatinineUnitChange],
  );

  const handleInputChange = useCallback(
    (key, value) => {
      setInputs((prev) => {
        const newInputs = {
          ...prev,
          [key]: value.replace?.(/^0+/, "") ?? value,
        };
        
        // if antibiotic is changed, recalculate immediately if all required fields are present
        if (key === "antibiotic" && showResult) { // Only if there's already a result showing
          // Use the same validation logic as validateFields function
          const heightValidationResult = validateHeightField(
            heightUnit,
            heightValue,
            feetValue,
            inchesValue,
          );
          const weightCheck = checkNum(weightValue, "weight");
          const weightSane = saneWeight(weightValue, weightUnit);
          const ageCheck = checkNum(newInputs.age, "age");
          const ageSane = saneAge(newInputs.age);
          const serumCheck = newInputs.renalUnknown ? "" : checkNum(serumCreatinineValue, "serum creatinine");
          const serumSane = newInputs.renalUnknown ? "" : saneSerum(serumCreatinineValue, serumCreatinineUnit);
          
          if (
            newInputs.antibiotic &&
            newInputs.sex &&
            heightValidationResult === "" &&
            weightCheck === "" &&
            weightSane === "" &&
            ageCheck === "" &&
            ageSane === "" &&
            serumCheck === "" &&
            serumSane === "" &&
            (newInputs.critical === true || newInputs.critical === false)
          ) {
            setTimeout(() => {
              const result = calculateAminoglycosideInitialDose({
                antibiotic: newInputs.antibiotic,
                sex: newInputs.sex,
                heightCm: getHeightInCm(
                  heightUnit,
                  heightValue,
                  feetValue,
                  inchesValue,
                ),
                actualWeightKg: getWeightInKg(weightValue, weightUnit),
                age: Number(newInputs.age),
                serumCreatinine: Number(serumCreatinineValue),
                serumCreatinineUnit: serumCreatinineUnit,
                renalUnknown: newInputs.renalUnknown,
                critical: newInputs.critical,
              });
              if (!result.error && result.dose) {
                setDose(result.dose);
                setCalcResult(result);
                setShowResult(true);
                setTimeout(() => {
                  if (resultsRef.current) {
                    resultsRef.current.scrollIntoView({
                      behavior: "smooth",
                      block: "start",
                    });
                  }
                }, 50);
              }
            }, 0);
          }
        }
        
        return newInputs;
      });
      setErrors((prev) => ({ ...prev, [key]: "" }));
    },
    [
      heightUnit,
      heightValue,
      feetValue,
      inchesValue,
      weightValue,
      weightUnit,
      serumCreatinineValue,
      serumCreatinineUnit,
      showResult,
    ],
  );

  const wrappedCreatinineChange = useCallback(
    (e) => {
      handleSerumCreatinineChange(e, setErrors);
      // Clear renalUnknown when user enters a creatinine value
      if (inputs.renalUnknown) {
        handleInputChange("renalUnknown", false);
      }
    },
    [handleSerumCreatinineChange, inputs.renalUnknown, handleInputChange],
  );

  function validateFields() {
    const errs = {};
    const validate = (value, label, ...validators) => {
      for (const fn of validators) {
        const result = fn(value, label);
        if (result !== "") return result;
      }
      return "";
    };

    if (!inputs.antibiotic) errs.antibiotic = "Please select an antibiotic";

    const heightValidationResult = validateHeightField(
      heightUnit,
      heightValue,
      feetValue,
      inchesValue,
    );
    if (heightValidationResult !== "") {
      errs.height = heightValidationResult;
    }

    const weightCheck = checkNum(weightValue, "weight");
    const weightSane = saneWeight(weightValue, weightUnit);
    if (weightCheck !== "") {
      errs.weight = weightCheck;
    } else if (weightSane !== "") {
      errs.weight = weightSane;
    }

    const ageCheck = validate(
      inputs.age,
      "age",
      (v) => checkNum(v, "age"),
      (v) => saneAge(v),
    );
    if (ageCheck !== "") errs.age = ageCheck;

    if (!inputs.sex) errs.sex = "Please select a sex";

    if (!inputs.renalUnknown) {
      const serumCheck = validate(
        serumCreatinineValue,
        "serum creatinine",
        (v) => checkNum(v, "serum creatinine"),
        (v) => saneSerum(v, serumCreatinineUnit),
      );
      if (serumCheck !== "") errs.serumCreatinine = serumCheck;
    }

    if (inputs.critical === null) errs.critical = "Please select yes or no";
    return errs;
  }

  function handleClear() {
    setInputs({ ...INITIAL_INPUTS });

    resetHeight();
    clearWeightFields();
    clearSerumCreatinineFields();
    setDose("");
    setShowResult(false);
    setCalcResult(null);
    setErrors({});
  }

  function handleCalculate(e) {
    e.preventDefault();
    const errs = validateFields();
    setErrors(errs);
    if (Object.keys(errs).length > 0) {
      setShowResult(false);
      setDose("");
      setCalcResult(null);
      return;
    }

    // Calculate height and weight values
    const heightCm = getHeightInCm(heightUnit, heightValue, feetValue, inchesValue);
    const actualWeightKg = getWeightInKg(weightValue, weightUnit);

    const result = calculateAminoglycosideInitialDose({
      antibiotic: inputs.antibiotic,
      sex: inputs.sex,
      heightCm: heightCm,
      actualWeightKg: actualWeightKg,
      age: Number(inputs.age),
      serumCreatinine: Number(serumCreatinineValue),
      serumCreatinineUnit: serumCreatinineUnit,
      renalUnknown: inputs.renalUnknown,
      critical: inputs.critical,
    });

    let errorField = null;
    let errorMsg = null;
    if (result.error) {
      if (/serum/i.test(result.error)) errorField = "serumCreatinine";
      else if (/weight/i.test(result.error)) errorField = "weight";
      else if (/height/i.test(result.error)) errorField = "height";
      else if (/age/i.test(result.error)) errorField = "height";
      errorMsg = result.error;
    } else if (!result.dose) {
      errorField = "general";
      errorMsg = "Calculation did not return a dose. Please check your inputs.";
    }

    if (errorField) {
      setErrors((prev) => ({ ...prev, [errorField]: errorMsg }));
      setShowResult(false);
      setDose("");
      setCalcResult(null);
      return;
    }

    setDose(result.dose);
    setCalcResult(result);
    
    setShowResult(true);
    setTimeout(() => {
      if (
        resultsRef.current &&
        typeof resultsRef.current.scrollIntoView === "function"
      ) {
        resultsRef.current.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      }
    }, 50);
  }

  const handleExpandDetails = () => {
    setTimeout(() => {
      if (calcDetailsRef.current) {
        calcDetailsRef.current.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      }
    }, 50);
  };

  const CriticalICUSelector = ({ value, onChange, error }) => (
    <div className={styles.formField}>
      <label className={styles.fieldLabel}>
        Septic shock or requires intensive care support:
      </label>
      <div className={styles.genderGroup} style={{ gap: 16 }}>
        <button
          type="button"
          className={`${styles.selectorBtn} ${value === true ? styles.selectorBtnActive : ""} ${error ? styles.inputContainerError : ""}`}
          aria-pressed={value === true}
          onClick={() => onChange(true)}
        >
          Yes
        </button>
        <button
          type="button"
          className={`${styles.selectorBtn} ${value === false ? styles.selectorBtnActive : ""} ${error ? styles.inputContainerError : ""}`}
          aria-pressed={value === false}
          onClick={() => onChange(false)}
        >
          No
        </button>
      </div>
      {error && <div className={styles.error}>{error}</div>}
    </div>
  );

  const panel = (
    <CalculatorPanel header="Calculator" onClear={handleClear} errors={{}}>
      <AntibioticsSelector
        value={inputs.antibiotic}
        onChange={(v) => handleInputChange("antibiotic", v)}
        error={errors.antibiotic}
      />

      <CalculatorGenderSelector
        value={inputs.sex}
        onChange={(v) => handleInputChange("sex", v)}
        error={errors.sex}
        btnClass={styles.selectorBtn}
        btnActiveClass={styles.selectorBtnActive}
      />

      <HeightInputGroup
        idPrefix="abx"
        height={heightValue}
        heightUnit={heightUnit}
        feet={feetValue}
        inches={inchesValue}
        onHeightChange={wrappedHeightChange}
        onFeetChange={wrappedFeetChange}
        onInchesChange={wrappedInchesChange}
        onUnitChange={wrappedUnitChange}
        error={errors.height}
        unitOptions={heightUnits}
        isWebLayout={isWebLayout}
      />

      <WeightInputGroup
        idPrefix="ag"
        label="Actual body weight"
        weightValue={weightValue}
        weightUnit={weightUnit}
        onWeightChange={wrappedWeightChange}
        onWeightUnitChange={wrappedWeightUnitChange}
        error={errors.weight}
        unitOptions={weightUnitOptions}
        isWebLayout={isWebLayout}
      />

      <CalculatorInput
        label="Age"
        id="ag-age"
        value={inputs.age}
        onChange={(e) => {
          handleInputChange("age", e.target.value);
        }}
        error={typeof errors.age === "string" ? errors.age : ""}
        unit="years"
        inputProps={{
          type: "number",
          min: 0,
          autoComplete: "off",
        }}
        isWebLayout={isWebLayout}
      />

      <CreatinineInputGroup
        creatinineValue={serumCreatinineValue}
        creatinineUnit={serumCreatinineUnit}
        onCreatinineChange={wrappedCreatinineChange}
        onCreatinineUnitChange={wrappedCreatinineUnitChange}
        error={errors.serumCreatinine}
        isWebLayout={isWebLayout}
      />

      <div className={styles.formField}>
        <label className={styles.fieldLabel + " " + styles.orLabel}>OR</label>
        <div
          className={
            styles.renalUnknownBox +
            (inputs.renalUnknown && !inputs.serumCreatinine
              ? " " + styles.renalUnknownBoxChecked
              : "")
          }
        >
          <span className={styles.renalUnknownText}>
            Kidney function is unknown, but <b>not</b> presumed to be impaired
          </span>
          <input
            type="radio"
            name="renal-status"
            checked={inputs.renalUnknown}
            onClick={() => {
              if (inputs.renalUnknown) {
                handleInputChange("renalUnknown", false);
              } else {
                handleInputChange("renalUnknown", true);
                setErrors((prev) => ({ ...prev, serumCreatinine: "" }));
                clearSerumCreatinineFields();
              }
            }}
            readOnly={true}
            className={styles.renalUnknownCheckbox}
          />
        </div>
      </div>

      <CriticalICUSelector
        value={inputs.critical}
        onChange={(v) => handleInputChange("critical", v)}
        error={errors.critical}
      />
      <div className={styles.calcBtnRow}>
        <button
          className={styles.calcBtn}
          type="submit"
          onClick={handleCalculate}
        >
          Calculate
        </button>
      </div>
    </CalculatorPanel>
  );

  const resultsLabel = inputs.antibiotic ? `${inputs.antibiotic} dose` : "Dose";
  const resultsUnit = dose ? "mg" : undefined;

  // dynamic calculation details for all antibiotics
  const calculationDetails =
    dose && showResult && calcResult ? (
      <div ref={calcDetailsRef} className={styles.calculationDetailsBlock}>
        <div className={styles.calculationDetailsText}>
          <strong>Administration advice</strong>
          <br />
          See{" "}
          <Link
            to="/viewTopic?etgAccess=true&guidelinePage=Antibiotic&topicfile=bartonella-infections&guidelinename=auto&sectionId=c_ABG_Aminoglycoside-dosing-and-administration_topic_2#c_ABG_Aminoglycoside-dosing-and-administration_topic_2"
            className={styles.resultLink}
          >
            Aminoglycoside administration advice.
          </Link>
        </div>
        <div className={styles.calculationDetailsText}>
          <strong>Dosing frequency</strong>
          <br />
          {inputs.critical
            ? `A single dose of ${inputs.antibiotic?.toLowerCase()} may be given; seek expert advice for subsequent management because the patient is critically ill.`
            : calcResult?.bmi && Number(calcResult.bmi) >= 35
              ? (
                <>
                  {`A single dose of ${inputs.antibiotic?.toLowerCase()} may be given; seek expert advice for subsequent management because the patient has a body mass index (BMI) of 35 kg/m² or more. For a discussion of different approaches to dosing in adults with obesity, see `}
                  <HashLink
                    to="/viewTopic?etgAccess=true&guidelinePage=Antibiotic&topicfile=bartonella-infections&guidelinename=auto&sectionId=c_ABG_Aminoglycoside-use-in-special-patient-groups_topic_3#c_ABG_Aminoglycoside-use-in-special-patient-groups_topic_3"
                    className={styles.resultLink}
                  >
                    Aminoglycoside use in adults who are obese
                  </HashLink>
                  .
                </>
              )
              : calcResult?.crcl && Number(calcResult.crcl) < 60
                ? `A single dose of ${inputs.antibiotic?.toLowerCase()} may be given; seek expert advice for subsequent management.`
                : inputs.antibiotic === "Amikacin"
                  ? "A maximum of 3 doses of amikacin can be given at 0, 24, and 48 hours."
                  : `A maximum of 3 doses of ${inputs.antibiotic?.toLowerCase()} can be given at 0, 24, and 48 hours.`}
        </div>
        <div className={styles.calculationDetailsText}>
          <strong>How this dose was derived</strong>
          <br />
          The calculated initial dose without rounding is{" "}
          {calcResult.rawDoseExact ? formatDoseDisplay(calcResult.rawDoseExact) : displayValue(calcResult.rawDose, "", 0)} mg
          {calcResult.isCapped ? " which exceeds the maximum dose. The maximum dose is recommended instead." : "."}          
          {!calcResult.isCapped && (
            <>
              {" "}For practicality, this dose has been rounded down to the nearest{" "}
              {inputs.antibiotic === "Amikacin" ? "125 mg" : "20 mg"}. For accuracy,
              rounding was only applied to the final value.
              <br />
            </>
          )}
          <br />
          The {inputs.antibiotic?.toLowerCase()} dose of{" "}
          {calcResult.rawDoseExact ? formatDoseDisplay(calcResult.rawDoseExact) : displayValue(calcResult.rawDose, "", 0)} mg corresponds to a dose of{" "}
          {displayValue(calcResult.mgPerKg, "mg/kg", 0)} based on a lean body
          weight of{" "}
          {isNumber(calcResult.lbw)
            ? formatWeightDisplay(Number(calcResult.lbw))
            : "-"}{" "}
          kg.
          <br />
          <br />
          {displayValue(calcResult.mgPerKg, "mg/kg", 0)}
          {inputs.critical
            ? " is recommended because the patient is critically ill."
            : inputs.renalUnknown
              ? " is recommended because the patient is not critically ill, and while their kidney function is unknown it is not presumed to be impaired."
              : ` is recommended because the patient is not critically ill and their creatinine clearance is ${calcResult.crcl > 120 ? "more than 120 mL/min" : displayValue(calcResult.crcl, "mL/min", 0)} (based on ${calcResult.weightTypeForCrCl || "adjusted body weight"}).`}
        </div>
      </div>
    ) : null;

  const results = (
    <div ref={resultsRef}>
      <CalculatorResults
        label={resultsLabel}
        value={dose}
        unit={resultsUnit}
        showResult={showResult}
        expandableContent={calculationDetails}
        onExpand={handleExpandDetails}
        isWebLayout={isWebLayout}
        footnote={
          <>
            <strong>Note:</strong> This calculator does not replace clinical
            judgement. Consider the appropriateness of the calculated dose for
            the patient.
          </>
        }
      />
    </div>
  );

  const description = (
    <div className={isWebLayout ? "" : styles.infoBlock}>
      <p>
        The calculator{isWebLayout ? "" : " below"} provides an initial aminoglycoside dose for
        patients older than 18 years. This calculator cannot be used to
        calculate the dose for surgical prophylaxis or synergistic therapy.
      </p>
      <p>The dose is calculated using the patient's lean body weight.</p>
      <p>
        For information on the selection of these parameters, and how to
        calculate them, see{" "}
        <Link
          to="/viewTopic?etgAccess=true&guidelinePage=Antibiotic&topicfile=c_ABG_Principles-of-aminoglycoside-use_topic_1&guidelinename=auto&sectionId=c_ABG_Principles-of-aminoglycoside-use_topic_1#c_ABG_Principles-of-aminoglycoside-use_topic_1"
          className={styles.resultLink}
        >
          Principles of aminoglycoside use,
        </Link>{" "}
        <Link to="/calculator/ccbwcalc" className={styles.resultLink}>
          Creatinine clearance calculator for adults
        </Link>{" "}
        and{" "}
        <Link to="/calculator/lbwcalc" className={styles.resultLink}>
          Lean body weight calculator
        </Link>
        .
      </p>
      <p>
        The calculator may be used for all adult patients without a
        contraindication for aminoglycoside use. Contraindications, as well as
        precautions for use, are listed in{" "}
        <HashLink
          to="/viewTopic?etgAccess=true&guidelinePage=Antibiotic&topicfile=bartonella-infections&guidelinename=auto&sectionId=c_ABG_Principles-of-aminoglycoside-use_topic_4#c_ABG_Principles-of-aminoglycoside-use_topic_4"
          className={styles.resultLink}
        >
          Figure: Aminoglycoside contraindications and precautions
        </HashLink>
        . A single dose of aminoglycoside is generally safe; however, subsequent
        dosing requires careful consideration in patients:
      </p>
      <ul>
        <li>
          with chronically impaired kidney function (adults with a creatinine
          clearance less than 60 mL/minute) who are not receiving long-term
          dialysis
        </li>
        <li>with rapidly deteriorating kidney function</li>
        <li>who are frail and elderly (eg 80 years or older).</li>
      </ul>
    </div>
  );

  const calculatorTitle = "Aminoglycoside initial dose calculator for adults";

  return (
    <CalculatorTemplate
      title={calculatorTitle}
      description={description}
      panel={panel}
      results={results}
      isWebLayout={isWebLayout}
    />
  );
};
export default AbxCalcApp;
